import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUpRight, Menu, X, Play, Shield, Crosshair, Users, Trophy, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { motion, useReducedMotion, useScroll, useTransform, AnimatePresence } from 'framer-motion';

import { useGetSiteContent } from "@workspace/api-client-react";
import type { SiteContent } from "@workspace/api-client-react";

const DEFAULT_DISCORD_URL = 'https://discord.gg/HaAvXEpWxK';
const ASSET_BASE = import.meta.env.BASE_URL;
const logoImg = `${ASSET_BASE}images/gbd-logo.webp`;
const heroImg = `${ASSET_BASE}images/gbd-hero-training-1024.webp`;
const heroImgSmall = `${ASSET_BASE}images/gbd-hero-training-640.webp`;
const practiceImg = `${ASSET_BASE}images/gbd-player-practice-640.webp`;
const detailImg = `${ASSET_BASE}images/gbd-training-detail-640.webp`;

function BrandLogo({ className = "h-10" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      <img
        src={logoImg}
        alt="GBD Team Logo"
        width="512"
        height="512"
        decoding="async"
        className="aspect-square h-full w-auto shrink-0 object-contain"
      />
      <span className="whitespace-nowrap font-display text-xl font-bold uppercase leading-none tracking-wider">
        GBD Team
      </span>
    </div>
  );
}

function Header({ discordUrl }: { discordUrl: string }) {
  const [open, setOpen] = useState(false);
  const navItems = [
    { label: 'Goal', href: '#goal' },
    { label: 'Tournaments', href: '#tournaments' },
    { label: 'Content', href: '#content' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-24 max-w-[1600px] items-center justify-between px-6 md:px-12">
        <a href="#top" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-background" aria-label="GBD Team Home">
          <BrandLogo />
        </a>
        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:text-white">
              {item.label}
            </a>
          ))}
        </nav>
        <a href={discordUrl} target="_blank" rel="noreferrer" className="hidden md:flex items-center gap-2 bg-primary px-6 py-3 text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-transform hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,0,85,0.4)]">
          Discord <ArrowUpRight size={14} strokeWidth={2.5} />
        </a>
        <button type="button" className="p-2 text-white md:hidden" onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-t border-white/5 bg-background/95 backdrop-blur-xl px-6 py-6 md:hidden absolute w-full" 
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} onClick={() => setOpen(false)} className="flex items-center justify-between border-b border-white/5 pb-4 text-xs font-bold uppercase tracking-widest text-white">
                  {item.label}
                  <ArrowUpRight size={14} className="text-primary" />
                </a>
              ))}
              <a href={discordUrl} target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-center gap-2 bg-primary px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-primary-foreground">
                Join Discord <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero({ discordUrl }: { discordUrl: string }) {
  const containerRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] });
  
  const width = useTransform(scrollYProgress, [0, 0.8], ['80%', '100%']);
  const height = useTransform(scrollYProgress, [0, 0.8], ['65vh', '76vh']);
  const borderRadius = useTransform(scrollYProgress, [0, 0.8], ['24px', '0px']);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  return (
    <section ref={containerRef} id="top" className="relative h-[155vh] bg-background">
      <div className="sticky top-0 flex h-screen w-full flex-col items-center justify-center gap-4 overflow-hidden">
        
        <motion.div 
          className="relative overflow-hidden z-10"
          style={{
            width: prefersReducedMotion ? '100%' : width,
            height: prefersReducedMotion ? '100vh' : height,
            borderRadius: prefersReducedMotion ? '0px' : borderRadius,
          }}
        >
          <img
            src={heroImg}
            srcSet={`${heroImgSmall} 640w, ${heroImg} 1024w`}
            sizes="(max-width: 768px) 100vw, 80vw"
            alt="Representative Rainbow Six Siege team training session"
            width="1024"
            height="1024"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-primary/10 mix-blend-overlay pointer-events-none" />
          
          <div className="absolute bottom-0 left-0 p-6 md:p-12 lg:p-16">
            <h1 className="font-display text-[clamp(3rem,7vw,8rem)] font-black uppercase leading-[0.85] tracking-tight">
              <span className="block text-white">Compete.</span>
              <span className="block text-primary">Create.</span>
              <span className="block text-accent">Connect.</span>
            </h1>
          </div>
        </motion.div>

        <motion.div 
          className="relative z-10 mx-auto flex w-[80%] max-w-[1600px] flex-col justify-between gap-3 sm:flex-row sm:items-end sm:gap-4"
          style={{ opacity: prefersReducedMotion ? 1 : textOpacity }}
        >
          <div className="flex flex-col gap-3">
            <div className="inline-flex w-fit items-center gap-2 border border-white/10 bg-black/50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-accent backdrop-blur-md">
              <span className="h-1.5 w-1.5 bg-accent animate-pulse" /> Rainbow Six Siege
            </div>
            <p className="hidden max-w-sm text-sm leading-relaxed text-white/70 sm:block">
              An emerging competitive team working towards the esports scene. Building our roster, refining strategies, and growing a dedicated community.
            </p>
          </div>
          <div className="flex gap-4">
            <a href={discordUrl} target="_blank" rel="noreferrer" className="inline-flex h-12 items-center justify-center gap-2 bg-primary px-6 text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-transform hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,0,85,0.4)]">
              Join Discord <ArrowUpRight size={14} strokeWidth={2.5} />
            </a>
            <a href="#goal" className="inline-flex h-12 items-center justify-center gap-2 border border-white/20 bg-white/5 px-6 text-[11px] font-bold uppercase tracking-widest text-white backdrop-blur-sm transition-colors hover:bg-white/10">
              Vision <ArrowDown size={14} strokeWidth={2.5} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SectionLabel({ text, color = "primary" }: { text: string, color?: "primary" | "accent" | "white" }) {
  const colorMap = {
    primary: "text-primary before:bg-primary",
    accent: "text-accent before:bg-accent",
    white: "text-white before:bg-white",
  };
  return (
    <h2 className={`mb-12 flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest ${colorMap[color]} before:block before:h-px before:w-8`}>
      {text}
    </h2>
  );
}

function GoalSection({ content }: { content: SiteContent }) {
  const { goal, roster } = content;
  
  return (
    <section id="goal" className="relative scroll-mt-28 overflow-hidden border-t border-white/5 bg-background py-24 md:py-32">
      <div className="pointer-events-none absolute -left-10 top-10 select-none opacity-[0.03] mix-blend-overlay">
        <span className="font-display text-[25vw] font-black uppercase leading-none tracking-tighter text-white">Vision</span>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-24">
          <div className="lg:col-span-6 xl:col-span-7">
            <SectionLabel text="Our Goal" color="primary" />
            <h3 className="font-display text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
              {goal.heading.split(' ').map((word, i, arr) => (
                <span key={i} className={i >= arr.length - 2 ? "text-stroke-primary text-transparent" : ""}>
                  {word}{" "}
                  {i === Math.floor(arr.length / 2) && <br className="hidden sm:block" />}
                </span>
              ))}
            </h3>
            <div className="mt-10 flex flex-col gap-6 border-l border-primary/20 pl-6 text-sm leading-relaxed text-white/70">
              {goal.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 xl:col-span-5 relative">
            <div className="absolute -inset-8 z-0 border border-white/5 bg-white/[0.01] pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-end justify-between border-b border-white/10 pb-4 mb-8">
                <div className="flex items-center gap-4">
                  <BrandLogo className="h-8 opacity-50 grayscale" />
                  <h4 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Active Roster</h4>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-primary shrink-0">In Formation</span>
              </div>
              
              <div className="flex flex-col gap-2">
                {roster.map((player, i) => {
                  const isActive = player.status === 'active';
                  
                  return (
                    <motion.div 
                      key={player.id} 
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className={`group flex items-center justify-between border ${isActive ? 'border-primary/30 bg-primary/10' : 'border-white/5 bg-black/40'} p-4 transition-colors hover:border-primary/50`}
                    >
                      <div className="flex items-center gap-4">
                        {player.imageUrl ? (
                          <div className="h-12 w-12 rounded-none border border-white/10 overflow-hidden bg-black/50 shrink-0">
                            <img src={player.imageUrl} alt="" width="48" height="48" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-80" />
                          </div>
                        ) : (
                          <span className={`font-display text-2xl font-bold ${isActive ? 'text-primary' : 'text-white/20'}`}>
                            0{i + 1}
                          </span>
                        )}
                        <div>
                          <div className={`text-sm font-bold uppercase tracking-wider ${isActive ? 'text-primary-foreground' : 'text-white'}`}>
                            {player.name}
                          </div>
                          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                            {player.role}
                            {player.handle && <span className="opacity-50">| {player.handle}</span>}
                          </div>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-2 border px-2 py-1 text-[0.6rem] font-bold uppercase tracking-widest shrink-0 ${
                        isActive ? 'border-primary bg-primary text-white' : 'border-primary/30 bg-primary/10 text-primary'
                      }`}>
                        <span className={`h-1 w-1 rounded-full ${isActive ? 'bg-white' : 'bg-primary animate-pulse'}`} /> 
                        {player.status}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              <p className="mt-8 text-xs leading-relaxed tracking-widest text-muted-foreground uppercase bg-primary/5 border border-primary/20 p-4">
                Think you have what it takes? We are actively scouting dedicated talent. Reach out via Discord to inquire about tryouts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TournamentsSection({ content }: { content: SiteContent }) {
  const { tournaments } = content;
  const contactUrl = content.recruitment?.contactUrl || DEFAULT_DISCORD_URL;

  // Use representative icons for fallback
  const getIcon = (format: string) => {
    if (format.toLowerCase().includes("cup")) return Trophy;
    if (format.toLowerCase().includes("custom")) return Crosshair;
    return Users;
  };

  return (
    <section id="tournaments" className="relative scroll-mt-28 overflow-hidden border-t border-white/5 bg-background py-24 md:py-32">
      <div className="pointer-events-none absolute -right-10 top-20 select-none opacity-[0.03] mix-blend-overlay">
        <span className="font-display text-[20vw] font-black uppercase leading-none tracking-tighter text-white">Compete</span>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionLabel text="Community Tournaments" color="accent" />
        
        <div className="mb-20 grid gap-10 lg:grid-cols-[1fr_400px] lg:items-end">
          <h3 className="font-display text-4xl font-bold uppercase leading-[0.9] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            More Than <br />Just A <span className="text-stroke-accent text-transparent">Team.</span>
          </h3>
          <p className="border-l border-accent/30 pl-6 text-sm leading-relaxed text-muted-foreground lg:pb-3">
            GBD Team organizes structured tournaments to give back to the scene. Compete for rewards, earn recognition, and experience high-stakes environments organized by players, for players.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tournaments.map((t, i) => {
            const Icon = getIcon(t.format);
            const isOpen = t.status === 'open';
            
            return (
              <motion.div 
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`group relative border bg-black/40 overflow-hidden transition-all hover:-translate-y-1 ${
                  isOpen ? 'border-accent/50 shadow-[0_10px_30px_-15px_rgba(0,255,255,0.2)]' : 'border-white/10 hover:border-white/30'
                }`}
              >
                {/* Image or fallback */}
                <div className="h-40 w-full overflow-hidden bg-black relative border-b border-white/10">
                  <img 
                    src={t.imageUrl || (i % 2 === 0 ? practiceImg : detailImg)} 
                    alt="" 
                    width="640"
                    height="640"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-luminosity transition-transform duration-700 group-hover:scale-105 group-hover:opacity-60" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-bold uppercase tracking-widest ${
                      isOpen ? 'bg-accent text-black' : 'bg-black/80 text-white border border-white/20'
                    }`}>
                      {isOpen && <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" />}
                      {t.status.replace('-', ' ')}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 text-[9px] font-bold uppercase tracking-widest bg-black/80 text-accent border border-accent/30 backdrop-blur-md">
                      {t.format}
                    </span>
                  </div>
                </div>

                <div className="p-6 md:p-8">
                  <h4 className="mb-3 font-display text-xl font-bold uppercase tracking-wide text-white">{t.title}</h4>
                  <p className="mb-6 text-xs leading-relaxed tracking-wider text-muted-foreground uppercase line-clamp-3">{t.description}</p>
                  
                  <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-auto">
                    <div className="flex flex-col gap-1">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Reward Pool</span>
                      <span className="text-xs font-bold uppercase tracking-widest text-white">{t.reward}</span>
                    </div>
                    {isOpen && (
                      <a href={contactUrl} target="_blank" rel="noreferrer" className="text-accent hover:text-white transition-colors">
                        <ArrowUpRight size={20} />
                      </a>
                    )}
                  </div>
                </div>
                
                <div className={`absolute bottom-0 left-0 h-0.5 w-0 transition-all duration-500 group-hover:w-full ${isOpen ? 'bg-accent' : 'bg-white/30'}`} />
              </motion.div>
            );
          })}
        </div>

        <div className="relative mt-16 overflow-hidden border border-accent/20 bg-black/60 p-12 text-center md:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-accent/10 to-transparent opacity-50" />
          <div className="relative z-10 flex flex-col items-center justify-center">
            <h4 className="mb-4 font-display text-3xl font-bold uppercase tracking-tight text-white">Ready To Compete?</h4>
            <p className="mb-8 max-w-2xl text-sm leading-relaxed tracking-wider text-muted-foreground uppercase">Tournament announcements, rulesets, and registration take place exclusively on our Discord server. Join the community to secure your spot in our next event.</p>
            <a href={contactUrl} target="_blank" rel="noreferrer" className="inline-flex h-14 items-center justify-center gap-2 bg-accent px-8 text-xs font-bold uppercase tracking-widest text-black transition-transform hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)]">
              Event Hub <ExternalLink size={16} strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContentSection({ content }: { content: SiteContent }) {
  const { media } = content;
  
  return (
    <section id="content" className="relative scroll-mt-28 overflow-hidden border-t border-white/5 bg-background py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none opacity-[0.02] mix-blend-overlay">
        <span className="font-display text-[25vw] font-black uppercase leading-none tracking-tighter text-white">Create</span>
      </div>

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
        <SectionLabel text="Clips & Content" color="white" />
        
        <div className="mb-16 grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-end">
          <h3 className="font-display text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Watch Us <br /><span className="text-stroke-muted text-transparent">Work.</span>
          </h3>
          <p className="border-l border-white/20 pl-6 text-sm leading-relaxed text-muted-foreground lg:pb-2">
            We are setting up our media pipeline to bring you high-quality gameplay, strategy breakdowns, and behind-the-scenes content from our practice sessions.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {media.map((item, i) => {
            const isPublished = item.status === 'published';
            const hasUrl = isPublished && item.url;
            const Wrapper = hasUrl ? 'a' : 'div';
            const wrapperProps = hasUrl ? { href: item.url, target: "_blank", rel: "noreferrer" } : {};
            
            // Assign primary color for clips, accent for articles, white for images
            const highlightColor = item.type === 'clip' ? 'primary' : item.type === 'article' ? 'accent' : 'white';
            const colorClass = highlightColor === 'primary' ? 'text-primary border-primary group-hover:text-primary group-hover:border-primary shadow-[0_0_30px_rgba(255,0,85,0.3)]' : 
                               highlightColor === 'accent' ? 'text-accent border-accent group-hover:text-accent group-hover:border-accent shadow-[0_0_30px_rgba(0,255,255,0.3)]' : 
                               'text-white border-white group-hover:text-white group-hover:border-white shadow-[0_0_30px_rgba(255,255,255,0.3)]';
                               
            const bgHoverClass = highlightColor === 'primary' ? 'group-hover:text-primary' : 
                                 highlightColor === 'accent' ? 'group-hover:text-accent' : 
                                 'group-hover:text-white';
                                 
            const fallbackImg = i % 2 === 0 ? practiceImg : detailImg;
            
            return (
              <Wrapper 
                key={item.id} 
                {...wrapperProps as any}
                className={`group relative aspect-[4/3] w-full overflow-hidden border border-white/10 bg-black block ${hasUrl ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <img 
                  src={item.imageUrl || fallbackImg} 
                  alt="" 
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-luminosity transition-all duration-1000 group-hover:scale-105 group-hover:opacity-70 group-hover:mix-blend-normal" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                
                <div className="absolute inset-x-0 top-6 flex items-center justify-center md:top-1/2 md:-translate-y-1/2">
                  <div className="flex items-center justify-center">
                    <span className={`flex h-16 w-16 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/70 backdrop-blur-md transition-all duration-500 group-hover:scale-110 ${isPublished ? colorClass : ''}`}>
                      <Play size={24} fill="currentColor" className="ml-1" />
                    </span>
                  </div>
                </div>
                {!isPublished && (
                  <span className="absolute right-4 top-4 border border-white/10 bg-black/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-md">
                    {item.status.replace('-', ' ')}
                  </span>
                )}

                <div className="absolute bottom-0 left-0 w-full p-8 md:p-10">
                  <div className="mb-4 flex items-center gap-4">
                    <div className={`h-px w-8 bg-${highlightColor === 'white' ? 'white' : highlightColor}`} />
                    <p className={`text-[10px] font-bold uppercase tracking-widest text-${highlightColor === 'white' ? 'white' : highlightColor}`}>
                      {item.type}
                    </p>
                  </div>
                  <h4 className={`font-display text-3xl font-bold uppercase tracking-wide text-white transition-colors ${hasUrl ? bgHoverClass : ''}`}>
                    {item.title}
                  </h4>
                  <p className="mt-2 text-xs uppercase tracking-widest text-muted-foreground line-clamp-2">{item.description}</p>
                </div>
              </Wrapper>
            );
          })}
          
          {media.length === 0 && (
            <div className="col-span-2 aspect-video w-full flex items-center justify-center border border-white/10 bg-black/40">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Media pipeline currently in setup phase.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ContactSection({ content }: { content: SiteContent }) {
  const { recruitment } = content;
  const discordUrl = recruitment.contactUrl || DEFAULT_DISCORD_URL;

  return (
    <section id="contact" className="relative scroll-mt-28 overflow-hidden border-t border-white/5 bg-background py-24 md:py-32">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="relative overflow-hidden border border-white/10 bg-black p-8 md:p-16 lg:p-24">
          <div className="pointer-events-none absolute right-0 top-0 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/3 rounded-full bg-primary/20 blur-[120px]" />
          
          <div className="relative z-10 grid gap-16 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="max-w-xl">
              <SectionLabel text="Recruitment & Contact" color="primary" />
              <h3 className="font-display text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
                {recruitment.heading.split(' ').map((word, i, arr) => (
                  <span key={i} className={i === arr.length - 1 ? "text-primary" : "text-white"}>
                    {word}{" "}
                    {i === Math.floor(arr.length / 2) && <br />}
                  </span>
                ))}
              </h3>
              <p className="mt-8 border-l border-primary/20 pl-6 text-sm leading-relaxed text-muted-foreground">
                {recruitment.description}
              </p>
            </div>
            
            <div className="flex w-full flex-col gap-4 sm:w-[320px]">
              <a href={discordUrl} target="_blank" rel="noreferrer" className={`group flex h-14 w-full items-center justify-between border ${recruitment.playerTryoutsOpen ? 'border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10 text-white' : 'border-white/10 bg-black/40 text-muted-foreground'} px-6 text-[11px] font-bold uppercase tracking-widest transition-all`}>
                <span className="flex items-center gap-3">
                  <span className={`h-2 w-2 rounded-full ${recruitment.playerTryoutsOpen ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  Player Tryouts
                </span>
                {recruitment.playerTryoutsOpen ? (
                  <ArrowUpRight size={16} className="text-primary transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                ) : (
                  <span className="text-[9px]">Closed</span>
                )}
              </a>
              
              <a href={discordUrl} target="_blank" rel="noreferrer" className={`group flex h-14 w-full items-center justify-between border ${recruitment.staffOpen || recruitment.creatorsOpen ? 'border-accent/30 bg-accent/5 hover:border-accent/50 hover:bg-accent/10 text-white' : 'border-white/10 bg-black/40 text-muted-foreground'} px-6 text-[11px] font-bold uppercase tracking-widest transition-all`}>
                <span className="flex items-center gap-3">
                  <span className={`h-2 w-2 rounded-full ${recruitment.staffOpen || recruitment.creatorsOpen ? 'bg-accent' : 'bg-muted-foreground/30'}`} />
                  Staff & Creators
                </span>
                {recruitment.staffOpen || recruitment.creatorsOpen ? (
                  <ArrowUpRight size={16} className="text-accent transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                ) : (
                  <span className="text-[9px]">Closed</span>
                )}
              </a>
              
              <div className="my-2 h-px w-full bg-white/10" />
              <a href={discordUrl} target="_blank" rel="noreferrer" className="flex h-14 w-full items-center justify-between bg-white px-6 text-[11px] font-bold uppercase tracking-widest text-black transition-transform hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                <span>Open Discord Server</span>
                <Shield size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-black py-12 md:py-16">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid gap-10 md:grid-cols-[1fr_auto_1fr] md:items-end">
          <div>
            <BrandLogo className="h-8 opacity-50 grayscale" />
            <p className="mt-6 max-w-sm text-xs leading-relaxed tracking-wider text-muted-foreground uppercase">
              An emerging Rainbow Six Siege team working toward the competitive scene.<br/>
              <span className="mt-2 block font-bold text-white/50">Compete. Create. Connect.</span>
            </p>
          </div>

          <a
            href="https://redragonshop.com/?aff=7190"
            target="_blank"
            rel="sponsored noreferrer"
            aria-label="Visit Redragon through the GBD Team affiliate link"
            className="group flex min-w-[220px] flex-col items-center gap-4 border border-white/10 bg-white/[0.03] px-8 py-6 text-center transition-all hover:-translate-y-1 hover:border-[#e60012]/50 hover:bg-[#e60012]/5 hover:shadow-[0_12px_40px_rgba(230,0,18,0.12)]"
          >
            <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-white/35">
              Affiliate
            </span>
            <img
              src="/redragon-logo.svg"
              alt="Redragon"
              width="98"
              height="58"
              loading="lazy"
              decoding="async"
              className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
            <span className="border border-[#e60012]/40 bg-[#e60012]/10 px-4 py-2 text-[9px] font-bold uppercase tracking-[0.2em] text-white/60">
              Use referral code <strong className="ml-1 text-xs tracking-[0.16em] text-white">GBDTEAM</strong>
            </span>
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/60 transition-colors group-hover:text-white">
              Shop with GBD Team
              <ArrowUpRight size={13} />
            </span>
            <span className="max-w-[210px] text-[8px] uppercase leading-relaxed tracking-wider text-white/25">
              Purchases through this link may support GBD Team.
            </span>
          </a>

          <div className="flex flex-col gap-6 md:items-end">
            <div className="flex items-center gap-6">
              <a href={DEFAULT_DISCORD_URL} target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-widest text-white transition-colors hover:text-primary">
                Discord Community
              </a>
            </div>
            <div className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-widest text-white/30 md:items-end">
              <span>© {new Date().getFullYear()} GBD Team. All rights reserved.</span>
              <span>Not affiliated with Ubisoft.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function GbdLanding() {
  const { data: content, isLoading, isError } = useGetSiteContent();
  const [minimumLoadingComplete, setMinimumLoadingComplete] = useState(false);

  useEffect(() => {
    const loadingTimer = window.setTimeout(() => {
      setMinimumLoadingComplete(true);
    }, 2000);

    return () => window.clearTimeout(loadingTimer);
  }, []);

  if (isLoading || !minimumLoadingComplete) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          <BrandLogo className="h-12 animate-pulse" />
          <Loader2 className="animate-spin text-primary" size={24} />
        </div>
      </div>
    );
  }

  if (isError || !content) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-6 text-center">
        <div className="flex max-w-md flex-col items-center gap-6 border border-white/10 bg-black/50 p-8 backdrop-blur-xl">
          <AlertTriangle className="text-primary" size={48} />
          <div>
            <h1 className="font-display text-2xl font-bold uppercase tracking-wide text-white mb-2">Network Error</h1>
            <p className="text-xs uppercase tracking-widest text-muted-foreground leading-relaxed">
              Unable to load team database. Please check your connection or try again later.
            </p>
          </div>
          <a href="/" className="mt-4 bg-white/10 hover:bg-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-widest text-white transition-colors">
            Retry Connection
          </a>
        </div>
      </div>
    );
  }

  const discordUrl = content.recruitment?.contactUrl || DEFAULT_DISCORD_URL;

  return (
    <main className="relative bg-background selection:bg-primary selection:text-white">
      <div className="site-noise" aria-hidden="true" />
      <Header discordUrl={discordUrl} />
      <Hero discordUrl={discordUrl} />
      <GoalSection content={content} />
      <TournamentsSection content={content} />
      <ContentSection content={content} />
      <ContactSection content={content} />
      <Footer />
    </main>
  );
}
